import moment from 'moment';
import {first} from './collect';
import {isObject} from './type-guards';
import {shortDaysOfWeek} from '../_constants/constants';

/*
 * A set of common string functions
 */

export function containsIgnoreCase(val: string, searchStr: string): boolean {
  if(!val || !searchStr) {
    return val === searchStr;
  }
  return val.trim().toLowerCase().indexOf(searchStr.toLowerCase()) >= 0;
}

export function startsWithIgnoreCase(val: string, searchStr: string): boolean {
  if(!val || !searchStr) {
    return val === searchStr;
  }
  return val.trim().toLowerCase().startsWith(searchStr.toLowerCase());
}

export function equalsIgnoreCase(s1: string, s2: string): boolean {
  if(!s1 || !s2) {
    return s1 === s2;
  }
  return s1.trim().toLowerCase() === s2.trim().toLowerCase();
}

export function equalsAny(s1: string, ...search: string[]): boolean {
  return !!search.find(value => s1 === value) || false;
}

export function equalsAnyIgnoreCase(s1: string, ...search: string[]): boolean {
  return !!search.find(value => equalsIgnoreCase(s1, value)) || false;
}

/**
 * Given a string and a chunk size, return an array of chunks from the string.
 * Eg: 'Hello' with chunk size 2 -> ['He', 'll', 'o']
 */
export function chunkSubstr(str: string, chunkSize: number): string[] {
  const numChunks = Math.ceil(str.length / chunkSize);
  const chunks = new Array(numChunks);

  for(let i = 0, o = 0 ; i < numChunks ; ++i, o += chunkSize) {
    chunks[i] = str.substr(o, chunkSize);
  }

  return chunks;
}

/**
 * Convert blank strings (eg: null, "", " ") into null.  It also works for recursive objects. <br/>
 * Eg: {a: "A", b: " "} becomes {a: "A", b: null}
 */
export function nullIfBlank<T>(s: T): T | null {
  if(typeof s === 'string') {
    return s?.toString()?.trim() === '' ? null : s;
  } else if(typeof s === 'number') {
    return s;
  } else if(isObject(s)) {
    return Object.fromEntries(Object.entries(s).map(e => [e[0], nullIfBlank(e[1])])) as T;
  } else {
    return null;
  }
}

/**
 * Convert a CSV-like string of key/value pairs to a map
 * Given a csv-like string "key1|value1|key2|value2", this method would return {key1: "value1", key2: "value2"}
 */
export function mapCsvKvp(csv: string, delimiter: string = '|'): {[key: string]: string} {
  if(!csv) {
    return null;
  }
  const map: {[key: string]: string} = {};
  const split: string[] = csv.split(delimiter);
  for(let k = 0, v = 1 ; v < split.length ; k++, v++) {
    map[split[k]] = split[v];
  }
  return map;
}

export function mapCsvKvpForCV4B(csv: string, delimiter: string = '|'): {[key: string]: string} {
  if(!csv) {
    return null;
  }
  const map: {[key: string]: string} = {};
  const split: string[] = csv.split(delimiter);
  for(let k = 0, v = 1 ; v < split.length ; k++, v++) {
    if(split[k] === 'CV4BSC100' || split[k] === 'CV4BSC200') {
      map[split[k]] = split[v];
    } else if(split[k] === 'CV4BSC207' || split[k] === 'CV4BSC208' || split[k] === 'CV4BSC209' || split[k] === 'CV4BSC210') {
      if(split[v] === 'true') {
        map[split[k]] = split[k] + '_Description';
        console.log(split[k] + '_Description');
      }
    } else {
      map[split[k]] = 'AACodes' + split[v] + '_CV4B';
    }

  }
  return map;
}

export function removeWhitespace(s: string): string {
  if(s === null) {
    return '';
  }
  return s.replace(/[\s]/g, '');
}

export function removeExtraWhitespace(s: string): string {
  return s.trim().replace(RegExp(' {2,}'), ' ');
}

// A better encoder from here: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent
export function encodeRFC5987ValueChars(str) {
  return encodeURIComponent(str).
    // Note that although RFC3986 reserves "!", RFC5987 does not,
    // so we do not need to escape it
    replace(/['()]/g, escape). // i.e., %27 %28 %29
    replace(/\*/g, '%2A').
    // The following are not required for percent-encoding per RFC5987,
    // so we can allow for a little better readability over the wire: |`^
    replace(/%(7C|60|5E)/g, (s, hex) => String.fromCharCode(parseInt(hex, 16)));
}

/**
 * Encodes an uppercase version of the parameter. Null-safe.
 */
export function uEncodeURI(nameParam: string): string {
  return nameParam && encodeRFC5987ValueChars(nameParam.toUpperCase());
}

/**
 * Null-safe uppercase
 */
export function upperCase(s: string): string {
  return s && s.toUpperCase();
}

export function matchesAny(v: string, searchStrings: string[]): boolean {
  return searchStrings && !!searchStrings.find(s => s === v);
}

/**
 * Determines if a string is null or contains nothing.  ie: ''
 */
export function isEmpty(v: string): boolean {
  return !v || v === '';
}

/**
 * Determines if a string is null or contains nothing but whitespace
 */
export function isBlank(v: string): boolean {
  return !v || v.trim() === '';
}

/**
 * Determines if a string is something other than null or whitespace
 */
export function isNotBlank(v: string): boolean {
  return !isBlank(v);
}

export function maskStart(toMask: string, numCharsToLeaveUnmasked = 4, maskChar = '*'): string {
  const maskRe = RegExp(`.(?=.{${numCharsToLeaveUnmasked < 0 ? 0 : numCharsToLeaveUnmasked}})`, 'g');
  return toMask.replace(maskRe, maskChar);
}

/**
 * This example returns a random integer between the specified values. The value is no lower than min (or the next integer greater than min if min isn't an integer),
 * and is less than (but not equal to) max.
 */
export function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
}

/*
 * Date functions
 */

function isBlankDate(date: moment.MomentInput) {
  return date == null || (typeof date === 'string' && isBlank(date));
}

export function removeTimeZone(date: moment.MomentInput | null): string | null {
  if(isBlankDate(date)) {
    return null;
  }
  date = typeof date === 'string' ? date : date.toString();
  return date.replace(/\+\d{4}$/g, '');
}

export function reformatDate(originalDate: moment.MomentInput, dateFormat?: string, inputFormat?: string): string {
  if(isBlankDate(originalDate) || originalDate === '00000000') {
    return null;
  }
  return moment(originalDate, inputFormat).format(dateFormat || 'YYYY-MM-DD');
}

export function reformatDateTime(originalDate: moment.MomentInput, dateFormat: string = 'YYYY-MM-DD HH:mm:ss.S', inputFormat?: string): string {
  return reformatDate(originalDate, dateFormat, inputFormat);
}

export function reformatDateMonth(originalDate: moment.MomentInput): string {
  if(isBlankDate(originalDate)) {
    return null;
  }
  return moment(originalDate).format('LLL');
}

export function reformatMonthDateYear(originalDate: moment.MomentInput): string {
  if(isBlankDate(originalDate)) {
    return null;
  }
  return moment(originalDate).format('LL');
}

export function currentTimeSeconds(): number {
  return Math.floor(Date.now() / 1000);
}

export function currentTimeFormatted(): string {
  return reformatDateTime(Date.now());
}

export function dayMonthYear(date: string, inputFormat?: string): string {
    return reformatDate(date, 'DDMMMYYYY', inputFormat);
}

/**
 * Given a day of the week, return the 1-based index.
 *
 * Eg:
 *  - sun => 1
 *  - tue => 3
 *  - sat => 6
 */
export function dayNumOfWeek(day: string) {
  //Offset to match the backend's 1-7 daynums
  return shortDaysOfWeek.indexOf(day) + 1;
}

/*
 * Reformatting functions
 */

export function reformatPhone(originalPhone: string | number): string {
  if(originalPhone == null) {
    return '';
  }
  const p = String(originalPhone);
  if(p.length === 10) {
    return p.replace(RegExp('(\\d{3})(\\d{3})(\\d{4})'), '($1) $2-$3');
  } else {
    return p;
  }
}

export function reformatPostal(originalPostal: string): string {
  if(originalPostal?.length === 6) {
    return originalPostal.toUpperCase().replace(RegExp('(.{3})(.{3})'), '$1-$2');
  } else {
    return originalPostal;
  }
}

export function reformatSin(originalSin: string): string {
  if(originalSin?.length === 9) {
    return originalSin.replace(RegExp('(.{3})(.{3})(.{3})'), '$1-$2-$3');
  } else {
    return originalSin;
  }
}

/*
 * Specific case format functions
 */

export function reformatReferenceId(referenceId: string): string {
  if(referenceId === null) {
    return '';
  }
  if(referenceId.length > 24) {
    return referenceId.slice(0, 24);
  } else {
    return referenceId;
  }
}

export function kob(memberCode: string): string {
  return first(RegExp('[a-z]{2,}', 'ig').exec(memberCode));
}

export function kobDefinitionI18nKey(memberCode: string): string {
  const _kob = kob(memberCode).toLowerCase();
  if(_kob === 'bb' || _kob === 'bc') {
    return `content.keyed.kob.${_kob}`;
  } else {
    return 'content.keyed.kob.other';
  }
}

export function mopI18nKey(mopCode: string): string {
  if(mopCode === null) {
    return null;
  }
  //If any single number (except 6) or if it's "UR"
  if((mopCode.length === 1 && '012345789'.indexOf(mopCode) >= 0) || 'UR' === mopCode) {
    return `content.keyed.mop.description.${mopCode}`;
  } else {
    return null;
  }
}

export function bankruptcyTypeI18nKey(bankruptcyCode: string | number): string {
  const _bankruptcyCode: number = Number(bankruptcyCode);
  if(!isNaN(_bankruptcyCode) && _bankruptcyCode >= 1 && _bankruptcyCode <= 4) {
    return `content.keyed.bankruptcies.type0${_bankruptcyCode}`;
  } else {
    return null;
  }
}

/**
 * Given a base string, and a list of strings, generate a map.  The map will use the list of strings as keys, and the values will be the base string + the key.
 *
 * Eg: i18nKeyMap("a.b.c", ["1", "2", "3"]) will generate:
 *
 * <pre>
 *    {
 *      "1": "a.b.c.1",
 *      "2": "a.b.c.2",
 *      "3": "a.b.c.3"
 *    }
 * </pre>
 *
 * You can pass an optional transformer-style method to manipulate the keys.
 *
 * Eg: i18nKeyMap("a.b.c", ["d", "e", "f"], toUpper) will generate:
 *
 * <pre>
 *    {
 *      "D": "a.b.c.d",
 *      "E": "a.b.c.e",
 *      "F": "a.b.c.f"
 *    }
 * </pre>
 *
 * @param baseKey - The string to be prepended to each of the keys' values when generating the map's values
 * @param keys - The list of strings that will act as the keys in the map and also be appended to the baseKey when generating the map's values
 * @param keyTransformer - Optional transformer function to modify key values.  Does <strong>NOT</strong> apply when building the values
 */
export function i18nKeyMap(baseKey: string, keys: string[], keyTransformer: (k) => string = (k) => k): {[key: string]: string} {
  // Use a ternary operator in case the keyTransformer is passed as null
  return Object.fromEntries(keys.map(k => [keyTransformer ? keyTransformer(k) : k, baseKey + k]));
}
